import { Injectable, signal } from '@angular/core';

import { MfaChallenge, MfaCredentials, MfaType } from './types';

@Injectable({ providedIn: 'root' })
export class MfaStateService {
  private readonly mfaTypeSignal = signal<MfaType | null>(null);
  private readonly destinationSignal = signal<string | null>(null);
  private readonly sessionIdSignal = signal<string | null>(null);
  private readonly deliveryValidSignal = signal(true);
  private readonly credentialsSignal = signal<MfaCredentials | null>(null);
  /** Absolute ms deadline for TOTP enrollment (QR start → code entry). */
  private readonly sessionExpiresAtSignal = signal<number | null>(null);

  readonly mfaType = this.mfaTypeSignal.asReadonly();
  readonly destination = this.destinationSignal.asReadonly();
  readonly sessionId = this.sessionIdSignal.asReadonly();
  readonly deliveryValid = this.deliveryValidSignal.asReadonly();
  readonly credentials = this.credentialsSignal.asReadonly();
  readonly sessionExpiresAt = this.sessionExpiresAtSignal.asReadonly();

  set(challenge: MfaChallenge): void {
    this.mfaTypeSignal.set(challenge.mfaType);
    this.destinationSignal.set(challenge.destination);
    this.sessionIdSignal.set(challenge.sessionId ?? null);
    this.deliveryValidSignal.set(challenge.deliveryValid ?? true);
    this.credentialsSignal.set(challenge.credentials ?? null);
    // New challenge must not inherit a leftover enrollment deadline (e.g.
    // abandoned QR without Continue/Return, then verify-only TOTP).
    this.sessionExpiresAtSignal.set(null);
  }

  /** Updates destination / session after a successful email/SMS resend. */
  updateDelivery(destination: string | null, sessionId?: string | null): void {
    this.destinationSignal.set(destination);
    if (sessionId !== undefined) {
      this.sessionIdSignal.set(sessionId);
    }
  }

  /**
   * Stores the enrollment session deadline so Continue to TOTP verify can
   * resume the same 15-minute clock (legacy parity).
   */
  setSessionExpiresAt(deadlineAt: number | null): void {
    this.sessionExpiresAtSignal.set(deadlineAt);
  }

  /**
   * Returns and clears the enrollment deadline for the verify screen.
   * Already-enrolled TOTP (no QR) finds null and starts a fresh 15 minutes.
   */
  consumeSessionExpiresAt(): number | null {
    const deadlineAt = this.sessionExpiresAtSignal();
    this.sessionExpiresAtSignal.set(null);
    return deadlineAt;
  }

  reset(): void {
    this.mfaTypeSignal.set(null);
    this.destinationSignal.set(null);
    this.sessionIdSignal.set(null);
    this.deliveryValidSignal.set(true);
    this.credentialsSignal.set(null);
    this.sessionExpiresAtSignal.set(null);
  }
}
